import React, { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  TablePagination,
  styled,
  tableCellClasses,
  Chip,
  TextField,
  Stack,
  Button,
  Box,
  Skeleton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import TextInput from "../InputComponents/TextInput";
import { Search, RestartAlt, Print } from "@mui/icons-material";
import CommonButton from "../InputComponents/CommonButton";
import { axiosAPI } from "../AxiosComponents/Axios";
import SelectInput from "../InputComponents/SelectInput";

import jsPDF from "jspdf";
import "jspdf-autotable";
import { useStaffLayout } from "../Context/CandidateLayoutContext";

// Styled table components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const AuditLog = ({ isAdmin }) => {
  const { privilegeData } = useStaffLayout();

  function hasAuditLogExport(privilegeData) {
    if (isAdmin) return true;
    if (privilegeData.length == 0) return true;
    return privilegeData.some((subModule) => {
      const isAuditLogExport = subModule.subModuleName.toLowerCase().includes("audit log");
      
      if (!isAuditLogExport) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() == "export" && priv.assigned == true
      );
    });
  }

  const [AuditLogData, setAuditLogData] = useState([]);
  const [fullAuditLogData, setFullAuditLogData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageCount, setPageCount] = useState("");
  const [loaderOpen, setLoaderOpen] = useState(true);

  const [filterData, setFilterData] = useState({
    user_name: null,
    event_id: null,
    start_date: null,
    end_date: null,
    status_id: null,
    ip_address: null,
  });

  const [eventList, setEventList] = useState([]);

  const [openConfirm, setOpenConfirm] = useState(false);

  const handleExportClick = () => {
    // Fetch the data before showing modal
    getFullAuditLogData(filterData);
    setOpenConfirm(true);
  };

  const handlePrint = () => {
    handlePrintAuditLog(); // Export
    setOpenConfirm(false); // Close modal
  };

  const handlePrintAuditLog = async () => {
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pdfTitle = `Audit Log - (${fullAuditLogData?.length} ${
        fullAuditLogData?.length == 1 ? "Record" : "Records"
      })`;
      const pdfFilename = `${pdfTitle}.pdf`;

      // Title
      doc.setFont("Geist, sans-serif", "bold");
      doc.setFontSize(14);
      doc.text(pdfTitle, 14, 15);
      doc.setFont("Geist, sans-serif", "normal");
      doc.setFontSize(10);

      // Prepare data exactly like your table UI
      const tableHead = [
        [
          "#",
          "Event",
          "Description",
          "Affected User / Item",
          "Performed By",
          "Date",
          "IP",
          "Status",
        ],
      ];

      const tableBody = fullAuditLogData.map((log, index) => {
        const formatEvent = (event) => {
          if (!event) return "-";
          return event
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
        };

        return [
          index + 1,
          formatEvent(log?.AL_Event),
          log?.AL_Description?.trim() || "-",
          log?.AL_AffectedItem_User?.trim() || "-",
          log?.AL_UserName?.trim() || "-",
          log?.AL_DateTime || "-",
          log?.AL_IpAddress || "-",
          log?.AL_Status || "-",
        ];
      });

      doc.autoTable({
        margin: { top: 25, left: 15, right: 15, bottom: 15 },
        startY: 25,
        head: tableHead,
        body: tableBody,
        styles: { fontSize: 9 },
        headStyles: {
          fillColor: [42, 110, 185],
          textColor: 255,
          fontStyle: "bold",
          halign: "left",
        },
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 30 },
          2: { cellWidth: 70 },
          3: { cellWidth: 40 },
          4: { cellWidth: 35 },
          5: { cellWidth: 35 },
          6: { cellWidth: 30 },
          7: { cellWidth: 25 },
        },
      });

      doc.save(pdfFilename);
    } catch (err) {
      console.error("PDF export failed:", err);
    }
  };

  const handleClose = () => {
    setOpenConfirm(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilterData = () => {
    setPage(0);
    const resetFilters = {
      user_name: null,
      event_id: null,
      start_date: null,
      end_date: null,
      status_id: null,
      ip_address: null,
    };

    setFilterData(resetFilters);
    getAuditLogData(resetFilters);
  };

  const handleApplyFilters = async () => {
    setPage(0);
    getAuditLogData(filterData);
  };

  const getAuditLogData = async (filters = filterData) => {
    const trimmedFilters = {
      user_name: filters?.user_name ? filters?.user_name?.trim() : null,
      event_id: filters?.event_id ? filters?.event_id : null,
      start_date: filters?.start_date ? filters?.start_date : null,
      end_date: filters?.end_date ? filters?.end_date : null,
      status_id: filters?.status_id ? filters?.status_id : null,
      ip_address: filters?.ip_address ? filters?.ip_address?.trim() : null,
    };
    await axiosAPI
      ?.post(`/Auditlog/${rowsPerPage}/${page + 1}/list`, trimmedFilters)
      .then((res) => {
        setPageCount(res?.data?.data?.total_count);
        setAuditLogData(res?.data?.data?.data);
        setLoaderOpen(false);
      })
      .catch((err) => {});
  };

  const getFullAuditLogData = async (filters = filterData) => {
    const trimmedFilters = {
      user_name: filters?.user_name ? filters?.user_name?.trim() : null,
      event_id: filters?.event_id ? filters?.event_id : null,
      start_date: filters?.start_date ? filters?.start_date : null,
      end_date: filters?.end_date ? filters?.end_date : null,
      status_id: filters?.status_id ? filters?.status_id : null,
      ip_address: filters?.ip_address ? filters?.ip_address?.trim() : null,
    };
    await axiosAPI
      ?.post(`/Auditlog/export/list`, trimmedFilters)
      .then((res) => {
        setFullAuditLogData(res?.data?.data?.data);
      })
      .catch((err) => {});
  };

  const getEventData = async () => {
    try {
      const res = await axiosAPI.get(`/Auditlog/auditEvents`);
      setEventList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getAuditLogData();
    getEventData();
  }, [page, rowsPerPage]);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        {/* Heading */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontFamily: "Geist, sans-serif",
          }}
        >
          Audit Log
        </Typography>

        {/* Export Button */}
        {hasAuditLogExport(privilegeData) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              ml: "auto",
            }}
          >
            <Button
              variant="contained"
              startIcon={<Print />}
              sx={{ background: "#006CB8" }}
              onClick={handleExportClick}
            >
              Export
            </Button>
          </Box>
        )}
      </Box>

      {/* Filters */}
      <Box sx={{ maxWidth: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          {/* Event */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Event
            </Typography>
            <SelectInput
              name="event_id"
              value={filterData?.event_id}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, event_id: e.target.value }))
              }
              options={eventList?.map((item) => ({
                value: item?.AEL_Id,
                label: item?.AEL_Name.replace(/_/g, " ")
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase()),
              }))}
              placeholder="Select Event"
              customStyle={{ width: "100%" }}
            />
          </Grid>

          {/* Performed By */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Performed By
            </Typography>
            <TextInput
              placeholder="Performed By"
              variant="outlined"
              size="small"
              name={`user_name`}
              value={filterData?.user_name ? filterData?.user_name : ""}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  user_name: e.target.value,
                }))
              }
              fullWidth
            />
          </Grid>

          {/* IP Address */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              IP address
            </Typography>
            <TextInput
              placeholder="IP Address"
              variant="outlined"
              size="small"
              name={`ip_address`}
              value={filterData?.ip_address ? filterData?.ip_address : ""}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  ip_address: e.target.value,
                }))
              }
              fullWidth
            />
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Start Date <span style={{ color: "red" }}> * </span>
            </Typography>
            <TextInput
              label="Start Date"
              placeholder="Start Date"
              name="start_date"
              value={filterData?.start_date}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  start_date: e.target.value,
                }))
              }
              type="date"
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              End Date <span style={{ color: "red" }}> * </span>
            </Typography>
            <TextInput
              label="Close Date"
              placeholder="Close Date"
              name="end_date"
              value={filterData?.end_date}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, end_date: e.target.value }))
              }
              type="date"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6} md={4} lg={4} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Status
            </Typography>
            <SelectInput
              value={filterData?.status_id}
              name="status_id"
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  status_id: e.target.value,
                }))
              }
              options={[
                { value: 1, label: "Success" },
                { value: 2, label: "Failed" },
              ]}
              placeholder="Select Status"
              customStyle={{ width: "100%" }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Search />}
          onClick={handleApplyFilters}
          sx={{
            textTransform: "capitalize",
            color: "white",
            background: "#006CB8",
            "&.Mui-disabled": {
              backgroundColor: "#a0a0a0", // custom grey or any color
              color: "#f5f5f5", // text/icon color when disabled
              borderColor: "#a0a0a0",
            },
          }}
        >
          Search
        </Button>

        <Button
          variant="outlined"
          startIcon={<RestartAlt />}
          onClick={handleResetFilterData}
          sx={{ textTransform: "capitalize" }}
        >
          Reset
        </Button>
      </Box>

      <Box
        sx={{
          borderRadius: "10px",
          overflowX: "auto",
          width: "100%",
          maxWidth: "100vw",
          margin: "auto",
        }}
      >
        {/* Table */}
        <TableContainer
          sx={{
            border: "1px solid transparent",
            borderRadius: "10px",
            maxWidth: "100vw",
            overflowX: "auto",
            margin: "auto",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ width:"50px" }}>#</StyledTableCell>
                <StyledTableCell>Event</StyledTableCell>
                <StyledTableCell sx={{ minWidth: "250px" }}>
                  {" "}
                  Description{" "}
                </StyledTableCell>
                <StyledTableCell>Affected User / Item</StyledTableCell>
                <StyledTableCell>Performed By</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell>IP</StyledTableCell>
                <StyledTableCell sx={{ width: "150px", textAlign: "center" }}>
                  Status
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loaderOpen ? (
                Array.from({ length: 7 }).map((_, idx) => (
                  <TableRow key={idx}>
                    {Array.from({ length: 8 }).map((_, index) => (
                      <StyledTableCell key={index}>
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height={40}
                        />
                      </StyledTableCell>
                    ))}
                  </TableRow>
                ))
              ) : AuditLogData && AuditLogData?.length > 0 ? (
                AuditLogData.map((log, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>
                      {index + 1 + page * rowsPerPage}
                    </StyledTableCell>
                    <StyledTableCell>
                      {log?.AL_Event
                        ? log.AL_Event.replace(/_/g, " ")
                            .toLowerCase()
                            .replace(/\b\w/g, (char) => char.toUpperCase())
                        : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {log?.AL_Description ? log?.AL_Description : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {log?.AL_AffectedItem_User
                        ? log?.AL_AffectedItem_User
                        : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {log?.AL_UserName ? log?.AL_UserName : "-"}
                    </StyledTableCell>

                    <StyledTableCell>
                      {log?.AL_DateTime ? log?.AL_DateTime : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {log?.AL_IpAddress ? log?.AL_IpAddress : "-"}
                    </StyledTableCell>

                    <StyledTableCell sx={{ textAlign: "center" }}>
                      <Chip
                        label={log?.AL_Status}
                        color={
                          log?.AL_Status === "Success" ? "success" : "error"
                        }
                        sx={{
                          fontSize: { xs: "10px", sm: "12px" },
                          width: "80px",
                        }}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={9} align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        py: 2,
                        fontSize: "20px",
                        fontFamily: "Geist, sans-serif",
                      }}
                    >
                      No data available
                    </Typography>
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <TablePagination
        component="div"
        count={pageCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          fontSize: { xs: "12px", sm: "14px" },
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif",
          },
        }}
      />

      <Dialog open={openConfirm} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: "bold",
            color: "#0977BC",
            mt: 3,
            mb: 2,
            fontFamily: "Geist, sans-serif",
          }}
        >
          Confirm Export
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                mb: 1,
                fontFamily: "Geist, sans-serif",
              }}
            >
              Would you like to export
              <strong> {fullAuditLogData?.length}</strong> audit log{" "}
              {fullAuditLogData?.length === 1 ? "record" : "records"}?
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ mt: 3, mb: 3, mr: 3 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <CommonButton
              variant="outlined"
              customColor="#B0BEC5"
              onClick={handleClose}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              customColor="#007BFF"
              onClick={handlePrint}
            >
              Yes
            </CommonButton>
          </Box>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AuditLog;
